import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Eye, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { SkeletonTable } from '../components/ui/skeleton';
import { ErrorCard } from '../components/error/ErrorCard';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { useLoans } from '../hooks/useQueries';
import { format } from 'date-fns';
import { formatCurrency } from '../utils/formatters';

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'pending':
      return <Clock className="h-4 w-4 text-orange-500" />;
    case 'approved':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'rejected':
      return <XCircle className="h-4 w-4 text-red-500" />;
    case 'active':
      return <AlertCircle className="h-4 w-4 text-blue-500" />;
    case 'paid':
      return <CheckCircle className="h-4 w-4 text-gray-500" />;
    default:
      return null;
  }
};

const getStatusColor = (status: string): "default" | "secondary" | "destructive" | "outline" => {
  switch (status) {
    case 'pending':
      return "outline";
    case 'approved':
    case 'paid':
      return "secondary";
    case 'rejected':
      return "destructive";
    case 'active':
      return "default";
    default:
      return "outline";
  }
};

export function LoansPage() {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  
  const { data: loansData, error, isLoading } = useLoans();

  const loans = loansData?.items || [];
  const filteredLoans = loans.filter((loan) => {
    const searchLower = searchTerm.toLowerCase();
    return loan.memberId?.toLowerCase().includes(searchLower);
  });

  const handleCreateLoan = () => {
    // TODO: Navigate to create loan page or open modal
  };

  const handleViewDetails = (loanId: string) => {
    // TODO: Navigate to loan details page
    console.log('View loan:', loanId);
  };

  const handleApproveLoan = (loanId: string) => {
    // TODO: Call approve loan API
    console.log('Approve loan:', loanId);
  };

  const handleRejectLoan = (loanId: string) => {
    // TODO: Call reject loan API
    console.log('Reject loan:', loanId);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('loans.title')}</h1>
          <p className="mt-2 text-gray-600">{t('loans.subtitle')}</p>
        </div>
        <SkeletonTable rows={5} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('loans.title')}</h1>
          <p className="mt-2 text-gray-600">{t('loans.subtitle')}</p>
        </div>
        <ErrorCard 
          error={error} 
          onRetry={() => window.location.reload()} 
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('loans.title')}</h1>
          <p className="mt-2 text-gray-600">{t('loans.subtitle')}</p>
        </div>
        <Button onClick={handleCreateLoan}>
          <Plus className="h-4 w-4 mr-2" />
          {t('loans.createLoan')}
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('common.search')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Input
            type="search"
            placeholder={t('loans.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
        </CardContent>
      </Card>

      {/* Loans Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('loans.table.loanId')}</TableHead>
                  <TableHead>{t('loans.table.member')}</TableHead>
                  <TableHead>{t('loans.table.amount')}</TableHead>
                  <TableHead>{t('loans.table.interestRate')}</TableHead>
                  <TableHead>{t('loans.table.monthlyPayment')}</TableHead>
                  <TableHead>{t('loans.table.startDate')}</TableHead>
                  <TableHead>{t('loans.table.endDate')}</TableHead>
                  <TableHead>{t('loans.table.status')}</TableHead>
                  <TableHead className="text-right">{t('loans.table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLoans.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-gray-500 py-8">
                      {t('common.noData')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLoans.map((loan) => (
                    <TableRow key={loan.loanId}>
                      <TableCell className="font-mono text-sm">
                        {loan.loanId?.slice(0, 8)}...
                      </TableCell>
                      <TableCell>{loan.memberId?.slice(0, 8)}...</TableCell>
                      <TableCell>{formatCurrency(loan.amount || 0)}</TableCell>
                      <TableCell>{loan.profitRate}%</TableCell>
                      <TableCell>{formatCurrency(loan.monthlyPayment || 0)}</TableCell>
                      <TableCell>
                        {loan.startDate ? format(new Date(loan.startDate), 'dd/MM/yyyy') : '-'}
                      </TableCell>
                      <TableCell>
                        {loan.endDate ? format(new Date(loan.endDate), 'dd/MM/yyyy') : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusColor(loan.status || '')}>
                          <span className="flex items-center gap-1">
                            {getStatusIcon(loan.status || '')}
                            {t(`loans.status.${loan.status}`)}
                          </span>
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewDetails(loan.loanId)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {loan.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-green-600 hover:text-green-700"
                                onClick={() => handleApproveLoan(loan.loanId)}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleRejectLoan(loan.loanId)}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('loans.summary.totalLoans')}</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{filteredLoans.length}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('loans.summary.activeLoans')}</CardTitle>
            <Clock className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {filteredLoans.filter((l) => l.status === 'active').length}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('loans.summary.pendingApproval')}</CardTitle>
            <Clock className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {filteredLoans.filter((l) => l.status === 'pending').length}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('loans.summary.totalAmount')}</CardTitle>
            <AlertCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(
                filteredLoans
                  .filter((l) => l.status === 'active' || l.status === 'approved')
                  .reduce((sum, loan) => sum + (loan.amount || 0), 0)
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}